import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Badge,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Progress,
  Input,
  Form,
  Table,
  Spinner
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import { translateActivity } from '../helpers/activityTranslator';
import { groupedAssetStatusOptions, getWorkTypeBadgeColor } from '../constants/utils';
import { useTranslation } from 'react-i18next';
import { WorkOrderStatusBadge } from './Common';
import StatusTimeline from './StatusTimeline';
import ToolCheckoutModal from './ToolCheckoutModal';
import ToolCheckinModal from './ToolCheckinModal';
import PermissionGuard from './Common/PermissionGuard';
import TimeEntrySection from './TimeEntrySection';
import WoCostSection from './WoCostSection';

const WorkOrderDetailsModal = ({ isOpen, toggle, workOrder, onEdit = null, onChecklistUpdate = null, workType = null }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('details');
  const [editingChecklistItem, setEditingChecklistItem] = useState(null);
  const [editedChecklistName, setEditedChecklistName] = useState('');
  const [editedRemarks, setEditedRemarks] = useState('');

  // Activity and Comments state
  const [activities, setActivities] = useState([]);
  const [activityForbidden, setActivityForbidden] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Sub-Work Orders state
  const [subWorkOrders, setSubWorkOrders] = useState([]);
  const [loadingSubWorkOrders, setLoadingSubWorkOrders] = useState(false);
  const [editingSubWorkOrder, setEditingSubWorkOrder] = useState(null);

  // Meters tab state
  const [woMeters, setWoMeters] = useState([]);
  const [woReadings, setWoReadings] = useState([]);
  const [loadingWoMeters, setLoadingWoMeters] = useState(false);
  const [recordReadingMeter, setRecordReadingMeter] = useState(null);
  const [readingValue, setReadingValue] = useState('');
  const [readingNotes, setReadingNotes] = useState('');
  const [submittingReading, setSubmittingReading] = useState(false);

  // Tools tab state
  const [requiredTools, setRequiredTools] = useState([]);
  const [checkedOutTools, setCheckedOutTools] = useState([]);
  const [loadingRequiredTools, setLoadingRequiredTools] = useState(false);
  const [loadingCheckedOutTools, setLoadingCheckedOutTools] = useState(false);
  const [newRequiredTool, setNewRequiredTool] = useState({ toolName: '', quantity: 1, notes: '' });
  const [toolCheckoutModal, setToolCheckoutModal] = useState(false);
  const [toolCheckinModal, setToolCheckinModal] = useState(false);
  const [toolCheckinId, setToolCheckinId] = useState(null);
  const [availableToolsSearch, setAvailableToolsSearch] = useState('');
  const [availableTools, setAvailableTools] = useState([]);
  const [showToolSearch, setShowToolSearch] = useState(false);
  const [selectedToolForCheckout, setSelectedToolForCheckout] = useState(null);

  /**
   * Get API endpoint prefix based on work order type
   * @param {string} type - Work order type (testing, routine, recall, calibration, etc.)
   * @returns {string} API endpoint prefix
   */
  const getEndpointPrefix = () => {
    const endpoints = {
      'testing': 'testing-requests',
      'routine': 'routine-requests',
      'recall': 'recall',
      'calibration': 'calibration',
      'reactive': 'asset-issues',
      'preventive': 'preventive'
    };

    // Use workType prop if provided, otherwise use workOrder.type or workOrder.workType
    const orderType = workType || workOrder?.type || workOrder?.workType;
    return endpoints[orderType?.toLowerCase()] || 'asset-issues';
  };

  const getActivity = async () => {
    if (!workOrder?.id) return;

    try {
      setLoadingActivities(true);
      const endpointPrefix = getEndpointPrefix();
      const { data } = await api.get(`${endpointPrefix}/activity?parentId=${workOrder?.id}`);
      setActivities(data.response || []);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!commentText.trim() || !workOrder?.id) {
      return;
    }

    try {
      setSubmittingComment(true);
      const endpointPrefix = getEndpointPrefix();
      const payload = {
        parentId: workOrder?.id,
        activity: commentText.trim()
      };

      await api.patch(`${endpointPrefix}/comment`, payload);

      // Clear the comment input
      setCommentText('');

      // Refresh activities to show the new comment
      await getActivity();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  // Fetch sub-work orders for recurring work orders
  const fetchSubWorkOrders = async () => {
    if (!workOrder?.id) return;

    try {
      setLoadingSubWorkOrders(true);
      const { data } = await api.get(`asset-issues/${workOrder.id}/sub-work-orders`);
      setSubWorkOrders(data.response || []);
    } catch (error) {
      console.error('Error fetching sub-work orders:', error);
      setSubWorkOrders([]);
    } finally {
      setLoadingSubWorkOrders(false);
    }
  };

  // Update sub-work order
  const handleUpdateSubWorkOrder = async (subWorkOrder) => {
    try {
      await api.put('asset-issues/sub-work-orders/update', {
        id: subWorkOrder.id,
        status: subWorkOrder.status,
        remarks: subWorkOrder.remarks,
        assignedTechnicianId: subWorkOrder.assignedTechnicianId,
        usageStatus: subWorkOrder.usageStatus
      });
      setEditingSubWorkOrder(null);
      await fetchSubWorkOrders();
    } catch (error) {
      console.error('Error updating sub-work order:', error);
    }
  };

  // Delete sub-work order
  const handleDeleteSubWorkOrder = async (id) => {
    if (!window.confirm(t('business.components.work_order_details.sub_work_orders.delete_confirm', 'Are you sure you want to delete this sub-work order?'))) return;

    try {
      await api.delete(`asset-issues/sub-work-orders/delete?id=${id}`);
      await fetchSubWorkOrders();
    } catch (error) {
      console.error('Error deleting sub-work order:', error);
    }
  };

  // Tools tab functions
  const fetchRequiredTools = async () => {
    if (!workOrder?.id) return;
    try {
      setLoadingRequiredTools(true);
      const { data } = await api.get(`work-order-tool/required/all?workOrderId=${workOrder.id}`);
      setRequiredTools(data?.response || []);
    } catch (error) {
      console.error('Error fetching required tools:', error);
      setRequiredTools([]);
    } finally {
      setLoadingRequiredTools(false);
    }
  };

  const fetchCheckedOutTools = async () => {
    if (!workOrder?.id) return;
    try {
      setLoadingCheckedOutTools(true);
      const { data } = await api.get(`work-order-tool/by-work-order?workOrderId=${workOrder.id}`);
      setCheckedOutTools(data?.response || []);
    } catch (error) {
      console.error('Error fetching checked out tools:', error);
      setCheckedOutTools([]);
    } finally {
      setLoadingCheckedOutTools(false);
    }
  };

  const handleAddRequiredTool = async () => {
    if (!newRequiredTool.toolName.trim()) return;
    try {
      await api.post('work-order-tool/required/add', [{
        workOrderId: workOrder.id,
        toolName: newRequiredTool.toolName.trim(),
        quantity: parseInt(newRequiredTool.quantity) || 1,
        notes: newRequiredTool.notes || ''
      }]);
      setNewRequiredTool({ toolName: '', quantity: 1, notes: '' });
      await fetchRequiredTools();
    } catch (error) {
      console.error('Error adding required tool:', error);
    }
  };

  const handleDeleteRequiredTool = async (id) => {
    try {
      await api.delete(`work-order-tool/required/delete?id=${id}`);
      await fetchRequiredTools();
    } catch (error) {
      console.error('Error deleting required tool:', error);
    }
  };

  const fetchAvailableTools = async (search) => {
    try {
      const { data } = await api.get(`tool/all?status=AVAILABLE&search=${encodeURIComponent(search)}&count=20`);
      setAvailableTools(data?.response || []);
    } catch (error) {
      console.error('Error fetching available tools:', error);
      setAvailableTools([]);
    }
  };

  const handleToolSearchChange = (value) => {
    setAvailableToolsSearch(value);
    if (value.length >= 2) {
      fetchAvailableTools(value);
      setShowToolSearch(true);
    } else {
      setShowToolSearch(false);
      setAvailableTools([]);
    }
  };

  const handleSelectToolForCheckout = (tool) => {
    setSelectedToolForCheckout(tool);
    setShowToolSearch(false);
    setAvailableToolsSearch('');
    setToolCheckoutModal(true);
  };

  const handleToolCheckoutSuccess = async () => {
    setToolCheckoutModal(false);
    setSelectedToolForCheckout(null);
    await fetchCheckedOutTools();
  };

  const handleToolCheckinFromWO = (checkoutId) => {
    setToolCheckinId(checkoutId);
    setToolCheckinModal(true);
  };

  const handleToolCheckinSuccess = async () => {
    setToolCheckinModal(false);
    setToolCheckinId(null);
    await fetchCheckedOutTools();
  };

  const formatToolDate = (date) => {
    if (!date) return '-';
    if (Array.isArray(date)) {
      const [y, m, d] = date;
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    } catch {
      return date;
    }
  };

  // Fetch activities when modal opens or when switching to activity tab
  useEffect(() => {
    if (isOpen && workOrder?.id && activeTab === 'activity') {
      getActivity();
    }
  }, [isOpen, workOrder?.id, activeTab]);

  // Fetch sub-work orders when switching to that tab
  useEffect(() => {
    if (isOpen && workOrder?.id && activeTab === 'subworkorders' && workOrder?.recurrenceType && workOrder.recurrenceType !== 'NONE') {
      fetchSubWorkOrders();
    }
  }, [isOpen, workOrder?.id, activeTab]);

  // Fetch tools when switching to tools tab
  useEffect(() => {
    if (isOpen && workOrder?.id && activeTab === 'tools') {
      fetchRequiredTools();
      fetchCheckedOutTools();
    }
  }, [isOpen, workOrder?.id, activeTab]);

  // Fetch meters when switching to meters tab (only if WO has an asset)
  useEffect(() => {
    if (isOpen && workOrder?.id && activeTab === 'meters') {
      const assetId = workOrder?.asset?.id || workOrder?.assetId;
      if (assetId) {
        setLoadingWoMeters(true);
        Promise.all([
          api.get(`meter/by-asset?assetId=${assetId}`),
          api.get(`meter/reading/by-work-order?workOrderId=${workOrder.id}`)
        ]).then(([metersRes, readingsRes]) => {
          setWoMeters(metersRes?.data?.response || []);
          setWoReadings(readingsRes?.data?.response || []);
        }).catch(() => {
          setWoMeters([]);
          setWoReadings([]);
        }).finally(() => setLoadingWoMeters(false));
      }
    }
  }, [isOpen, workOrder?.id, workOrder?.asset?.id, workOrder?.assetId, activeTab]);

  const handleRecordReading = async () => {
    if (!recordReadingMeter || !readingValue) return;
    try {
      setSubmittingReading(true);
      await api.post('meter/reading/create', {
        meterId: recordReadingMeter.id,
        value: parseFloat(readingValue),
        source: 'WORK_ORDER',
        workOrderId: workOrder.id,
        notes: readingNotes || null,
      });
      // Refresh meters and readings
      const assetId = workOrder?.asset?.id || workOrder?.assetId;
      const [metersRes, readingsRes] = await Promise.all([
        api.get(`meter/by-asset?assetId=${assetId}`),
        api.get(`meter/reading/by-work-order?workOrderId=${workOrder.id}`)
      ]);
      setWoMeters(metersRes?.data?.response || []);
      setWoReadings(readingsRes?.data?.response || []);
      setRecordReadingMeter(null);
      setReadingValue('');
      setReadingNotes('');
    } catch (error) {
      console.error('Failed to record reading:', error);
    } finally {
      setSubmittingReading(false);
    }
  };

  const METER_STATUS_COLORS = {
    PENDING: 'secondary',
    STABLE: 'success',
    WARNING: 'warning',
    ALARM: 'danger',
    OVERDUE: 'info',
  };

  const renderMetersTab = () => {
    const assetId = workOrder?.asset?.id || workOrder?.assetId;
    if (!assetId) {
      return (
        <div className="text-center py-5">
          <FeatherIcon icon="alert-circle" size={48} className="text-muted mb-3" />
          <h5 className="text-muted">{t('meters.no_asset_linked', 'No asset linked')}</h5>
          <p className="text-muted">{t('meters.wo_needs_asset', 'This work order must be linked to an asset to show meters.')}</p>
        </div>
      );
    }

    if (loadingWoMeters) {
      return (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="text-muted mt-2">{t('shared.loading', 'Loading...')}</p>
        </div>
      );
    }

    return (
      <div className="p-4">
        {/* Inline record reading form */}
        {recordReadingMeter && (
          <Card className="border-warning mb-4">
            <CardBody>
              <h6 className="mb-3">
                {t('meters.record_reading', 'Record Reading')}: <strong>{recordReadingMeter.name}</strong>
              </h6>
              <Row>
                <Col md={4}>
                  <Input
                    type="number"
                    step="any"
                    placeholder={t('meters.reading_value', 'Value')}
                    value={readingValue}
                    onChange={(e) => setReadingValue(e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Input
                    type="text"
                    placeholder={t('meters.reading_notes', 'Notes (optional)')}
                    value={readingNotes}
                    onChange={(e) => setReadingNotes(e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Button
                    color="primary"
                    onClick={handleRecordReading}
                    disabled={!readingValue || submittingReading}
                    className="me-2"
                  >
                    {submittingReading ? <Spinner size="sm" /> : t('shared.save', 'Save')}
                  </Button>
                  <Button color="light" onClick={() => { setRecordReadingMeter(null); setReadingValue(''); setReadingNotes(''); }}>
                    {t('shared.cancel', 'Cancel')}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Asset Meters */}
        {woMeters.length > 0 ? (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody>
              <h6 className="mb-3">{t('meters.title', 'Meters')}</h6>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>{t('meters.name', 'Name')}</th>
                      <th>{t('meters.measurement_unit', 'Unit')}</th>
                      <th>{t('meters.last_reading', 'Last Reading')}</th>
                      <th>{t('meters.status', 'Status')}</th>
                      <th>{t('meters.actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {woMeters.map((meter) => (
                      <tr key={meter.id}>
                        <td className="fw-medium">{meter.name}</td>
                        <td>{meter.measurementUnit || '-'}</td>
                        <td>
                          {meter.lastReadingValue != null
                            ? `${meter.lastReadingValue} ${meter.measurementUnit || ''}`
                            : '-'}
                        </td>
                        <td>
                          <Badge color={METER_STATUS_COLORS[meter.status] || 'secondary'}>
                            {meter.status}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            color="soft-primary"
                            size="sm"
                            onClick={() => { setRecordReadingMeter(meter); setReadingValue(''); setReadingNotes(''); }}
                          >
                            <FeatherIcon icon="edit-3" size={14} className="me-1" />
                            {t('meters.record_reading', 'Record Reading')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        ) : (
          <div className="text-center py-4 mb-4">
            <FeatherIcon icon="activity" size={48} className="text-muted mb-3" />
            <h5 className="text-muted">{t('meters.no_meters', 'No meters found')}</h5>
          </div>
        )}

        {/* Readings recorded on this WO */}
        {woReadings.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardBody>
              <h6 className="mb-3">{t('meters.readings_on_wo', 'Readings recorded on this work order')}</h6>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>{t('meters.name', 'Meter')}</th>
                      <th>{t('meters.reading_value', 'Value')}</th>
                      <th>{t('meters.status', 'Status')}</th>
                      <th>{t('meters.recorded_by', 'Recorded By')}</th>
                      <th>{t('meters.date', 'Date')}</th>
                      <th>{t('meters.reading_notes', 'Notes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {woReadings.map((reading) => (
                      <tr key={reading.id}>
                        <td className="fw-medium">{reading.meterName}</td>
                        <td>{reading.value}</td>
                        <td>
                          <Badge color={METER_STATUS_COLORS[reading.resultingStatus] || 'secondary'}>
                            {reading.resultingStatus}
                          </Badge>
                        </td>
                        <td>{reading.recordedByName || '-'}</td>
                        <td>{reading.createdAt ? new Date(reading.createdAt).toLocaleDateString('en-GB') : '-'}</td>
                        <td>{reading.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    );
  };

  if (!workOrder) return null;

  // Animation variants
  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: -50
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };


  // Get priority badge
  const getPriorityBadge = (urgency) => {
    const priorityConfigs = {
      'HIGH': { color: 'danger', icon: 'alert-triangle' },
      'MEDIUM': { color: 'warning', icon: 'alert-circle' },
      'LOW': { color: 'success', icon: 'check-circle' }
    };

    const config = priorityConfigs[urgency] || { color: 'secondary', icon: 'help-circle' };

    return (
      <Badge
        color={config.color}
        className="badge-soft d-inline-flex align-items-center"
        style={{ fontSize: '11px', padding: '4px 8px' }}
      >
        <FeatherIcon icon={config.icon} size={12} className="me-1" />
        {urgency ? t(`shared.${urgency.toLowerCase()}`, urgency.replace(/_/g, ' ')) : t('shared.not_set', 'Not Set')}
      </Badge>
    );
  };

  // Get progress for status
  const getStatusProgress = (status) => {
    const progressMap = {
      'SUBMITTED': 20,
      'PENDING': 40,
      'IN_PROGRESS': 60,
      'ACTIVE': 60,
      'ON_HOLD': 30,
      'COMPLETED': 100,
      'RESOLVED': 100
    };
    return progressMap[status] || 0;
  };

  const formatCreatedDate = (dateString) => {
    if (!dateString) return t('shared.n_a', 'N/A');
    return new Date(dateString).toLocaleString();
  };

  const getWorkOrderIcon = (type) => {
    const iconMap = {
      'work-order': 'tool',
      'calibration': 'settings',
      'testing': 'zap',
      'routine': 'refresh-cw',
      'recall': 'alert-triangle',
      'preventive': 'calendar'
    };
    return iconMap[type] || 'clipboard';
  };

  const getUsageStatusLabel = (status) => {
    if (!status) return t('shared.n_a', 'N/A');
    for (const group of groupedAssetStatusOptions(t)) {
      const option = group.options.find(opt => opt.value === status);
      if (option) return option.label;
    }
    return status;
  };

  const getWorkOrderColor = (type) => {
    const colorMap = {
      'work-order': '#4285f4',    // Google Blue
      'calibration': '#ea4335',   // Google Red
      'testing': '#fbbc04',       // Google Yellow
      'routine': '#34a853',       // Google Green
      'recall': '#9334e6',        // Purple
      'preventive': '#ff6d00'     // Orange
    };
    return colorMap[type] || '#4285f4';
  };

  const renderBasicInfoTab = () => (
    <div className="p-4">
      {/* Basic Information Cards */}
      <Row className="g-4 mb-4">
        {/* Basic Information */}
        <Col md={6}>
          <motion.div variants={cardVariants} initial="hidden" animate="visible" className="h-100">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="info-icon me-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '40px', height: '40px' }}>
                      <FeatherIcon icon="info" size={20} className="text-primary" />
                    </div>
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold">{t('shared.basic_information', 'Basic Information')}</h6>
                    <p className="text-muted small mb-0">{t('business.components.work_order_details.basic_info.subtitle', 'Core work order details')}</p>
                  </div>
                </div>

                <div className="info-grid">
                  {workOrder.ticketNumber && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.ticket_number', 'Ticket Number')}</span>
                      </div>
                      <p className="mb-0 text-dark">{workOrder.ticketNumber}</p>
                    </div>
                  )}

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="file-text" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.title', 'Title')}</span>
                    </div>
                    <p className="mb-0 text-dark">{workOrder.eventTitle || workOrder.title || t('shared.no_title', 'No title')}</p>
                  </div>

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="calendar" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.created_date', 'Created Date')}</span>
                    </div>
                    <p className="mb-0 text-dark">{formatCreatedDate(workOrder.createdAt)}</p>
                  </div>

                  {workOrder.workType && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="tag" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.work_type', 'Work Type')}</span>
                      </div>
                      <Badge color={getWorkTypeBadgeColor(workOrder.workType)}>{workOrder.workType}</Badge>
                    </div>
                  )}

                  {workOrder.dueDate && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="clock" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.due_date', 'Due Date')}</span>
                      </div>
                      <p className="mb-0 text-dark">{formatCreatedDate(workOrder.dueDate)}</p>
                    </div>
                  )}

                  {workOrder.usageStatus && (
                    <div className="info-item mb-0">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="activity" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.usage_status', 'Usage Status')}</span>
                      </div>
                      <Badge color="primary" style={{ fontSize: '12px', padding: '6px 12px' }}>
                        {getUsageStatusLabel(workOrder.usageStatus)}
                      </Badge>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </motion.div>
        </Col>

        {/* Issue/Work Details */}
        <Col md={6}>
          <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }} className="h-100">
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="p-4">
                <div className="d-flex align-items-center mb-3">
                  <div className="info-icon me-3">
                    <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '40px', height: '40px' }}>
                      <FeatherIcon icon={getWorkOrderIcon(workOrder.type)} size={20} className="text-warning" />
                    </div>
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold">{t('business.components.work_order_details.type_details_title', '{{type}} Details', { type: workOrder.type?.toUpperCase().replace('-', ' ') })}</h6>
                    <p className="text-muted small mb-0">{t('business.components.work_order_details.type_details_subtitle', 'Work order specific information')}</p>
                  </div>
                </div>

                <div className="info-grid">
                  {workOrder.description && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="file-text" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.description', 'Description')}</span>
                      </div>
                      <div className="bg-light rounded p-2">
                        <p className="mb-0 text-dark small">
                          {workOrder.description || t('shared.no_description_provided', 'No description provided')}
                        </p>
                      </div>
                    </div>
                  )}

                  {(workOrder.comments || workOrder.comment) && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="message-square" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.comments', 'Comments')}</span>
                      </div>
                      <div className="bg-light rounded p-2">
                        <p className="mb-0 text-dark small">
                          {workOrder.comments || workOrder.comment || t('shared.no_comments_provided', 'No comments provided')}
                        </p>
                      </div>
                    </div>
                  )}

                  {workOrder.urgencyLevel && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="zap" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.urgency_level', 'Urgency Level')}</span>
                      </div>
                      <div>{getPriorityBadge(workOrder.urgencyLevel)}</div>
                    </div>
                  )}

                  {workOrder.usageStatus && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="activity" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.usage_status', 'Usage Status')}</span>
                      </div>
                      <p className="mb-0 text-dark">{workOrder.usageStatus?.replace(/_/g, ' ') || t('shared.not_specified', 'Not specified')}</p>
                    </div>
                  )}

                  {workOrder.estimatedTime && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="clock" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.estimated_time', 'Estimated Time')}</span>
                      </div>
                      <p className="mb-0 text-dark">{workOrder.estimatedTime}</p>
                    </div>
                  )}

                  <div className="info-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <FeatherIcon icon="calendar" size={14} className="text-muted me-2" />
                      <span className="fw-semibold text-muted small">{t('shared.due_date', 'Due Date')}</span>
                    </div>
                    <p className="mb-0 text-dark">
                      {workOrder.requestDate
                        ? formatCreatedDate(workOrder.requestDate)
                        : t('shared.not_specified', 'Not specified')
                      }
                    </p>
                  </div>

                  {/* Preventive Maintenance specific fields */}
                  {workOrder.type === 'preventive' && workOrder.pmCategory && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="tag" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.pm_category_label', 'PM Category')}</span>
                      </div>
                      <Badge color="info" style={{ fontSize: '12px', padding: '6px 12px' }}>
                        {workOrder.pmCategory.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  )}

                  {workOrder.type === 'preventive' && workOrder.pmFrequency && (
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="repeat" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.pm_frequency_label', 'PM Frequency')}</span>
                      </div>
                      <Badge color="primary" style={{ fontSize: '12px', padding: '6px 12px' }}>
                        {workOrder.pmFrequency.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  )}

                  {workOrder.type === 'preventive' && workOrder.scheduledDate && (
                    <div className="info-item mb-0">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="calendar" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.scheduled_date', 'Scheduled Date')}</span>
                      </div>
                      <p className="mb-0 text-dark">{workOrder.scheduledDate}</p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Tags and Additional Info */}
      {workOrder.issueType && workOrder.issueType.length > 0 && (
        <Row className="g-4 mb-4">
          <Col>
            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="info-icon me-3">
                      <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <FeatherIcon icon="tag" size={20} className="text-info" />
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">{t('business.components.work_order_details.tags_categories.title', 'Tags & Categories')}</h6>
                      <p className="text-muted small mb-0">{t('business.components.work_order_details.tags_categories.subtitle', 'Issue types')}</p>
                    </div>
                  </div>

                  <Row>
                    <Col md={6}>
                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <FeatherIcon icon="layers" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.issue_type', 'Issue Type')}</span>
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {workOrder.issueType.map((type, index) => (
                            <Badge key={index} color="light" className="text-dark">
                              {type.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </motion.div>
          </Col>
        </Row>
      )}
    </div>
  );

  const renderAssetTab = () => (
    <div className="p-4">
      <Row className="g-4">
        {/* Asset Information */}
        {workOrder.asset && (
          <Col md={6}>
            <motion.div variants={cardVariants} initial="hidden" animate="visible">
              <Card className="border-0 shadow-sm h-100">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="info-icon me-3">
                      <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <FeatherIcon icon="package" size={20} className="text-success" />
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">{t('shared.asset_details', 'Asset Details')}</h6>
                      <p className="text-muted small mb-0">{t('shared.equipment_information', 'Equipment information')}</p>
                    </div>
                  </div>

                  <div className="info-grid">
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="box" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.asset_name', 'Asset Name')}</span>
                      </div>
                      <p className="mb-0 text-dark">{workOrder.asset.assetName || workOrder.asset.name || workOrder.assetId?.name || t('shared.not_specified', 'Not specified')}</p>
                    </div>

                    {workOrder.asset.assetNumber && (
                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center mb-1">
                          <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.asset_number', 'Asset Number')}</span>
                        </div>
                        <p className="mb-0 text-dark">{workOrder.asset.assetNumber}</p>
                      </div>
                    )}

                    {workOrder.asset.serialNumber && (
                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center mb-1">
                          <FeatherIcon icon="layers" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.serial_number', 'Serial Number')}</span>
                        </div>
                        <p className="mb-0 text-dark">{workOrder.asset.serialNumber}</p>
                      </div>
                    )}

                    {workOrder.asset.manufacturer && (
                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center mb-1">
                          <FeatherIcon icon="building" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.manufacturer', 'Manufacturer')}</span>
                        </div>
                        <p className="mb-0 text-dark">{workOrder.asset.manufacturer}</p>
                      </div>
                    )}

                    {(workOrder.asset?.currentLocation || workOrder.locationName) && (
                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center mb-1">
                          <FeatherIcon icon="map-pin" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.location', 'Location')}</span>
                        </div>
                        <p className="mb-0 text-dark">{workOrder.asset?.currentLocation || workOrder.locationName}</p>
                      </div>
                    )}

                    {workOrder.asset.id && (
                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center mb-1">
                          <FeatherIcon icon="hash" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.asset_id', 'Asset ID')}</span>
                        </div>
                        <p className="mb-0 text-dark">{workOrder.asset.id}</p>
                      </div>
                    )}

                    {workOrder.asset.assetStatus && (
                      <div className="info-item mb-0">
                        <div className="d-flex align-items-center mb-1">
                          <FeatherIcon icon="activity" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.asset_status', 'Asset Status')}</span>
                        </div>
                        <Badge color="secondary">
                          {workOrder.asset.assetStatus.replace('_', ' ')}
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          </Col>
        )}

        {/* Organization Information */}
        {workOrder.organization && (
          <Col md={6}>
            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
              <Card className="border-0 shadow-sm h-100">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="info-icon me-3">
                      <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <FeatherIcon icon="home" size={20} className="text-info" />
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">{t('shared.organization_information', 'Organization Information')}</h6>
                      <p className="text-muted small mb-0">{t('business.components.work_order_details.organization_info.subtitle', 'Location and contact details')}</p>
                    </div>
                  </div>

                  <div className="info-grid">
                    <div className="info-item mb-3">
                      <div className="d-flex align-items-center mb-1">
                        <FeatherIcon icon="building" size={14} className="text-muted me-2" />
                        <span className="fw-semibold text-muted small">{t('shared.organization_name', 'Organization Name')}</span>
                      </div>
                      <p className="mb-0 text-dark">{workOrder.organization.name}</p>
                    </div>

                    {workOrder.organization.address && (
                      <div className="info-item mb-3">
                        <div className="d-flex align-items-center mb-1">
                          <FeatherIcon icon="map-pin" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.address', 'Address')}</span>
                        </div>
                        <p className="mb-0 text-dark">{workOrder.organization.address}</p>
                      </div>
                    )}

                    {workOrder.organization.contactNumber && (
                      <div className="info-item mb-0">
                        <div className="d-flex align-items-center mb-1">
                          <FeatherIcon icon="phone" size={14} className="text-muted me-2" />
                          <span className="fw-semibold text-muted small">{t('shared.contact', 'Contact')}</span>
                        </div>
                        <p className="mb-0 text-dark">{workOrder.organization.contactNumber}</p>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          </Col>
        )}
      </Row>
    </div>
  );

  const renderTechniciansTab = () => {
    const getTechnicians = () => {
      if (workOrder.assignedTechnician && Array.isArray(workOrder.assignedTechnician)) {
        return workOrder.assignedTechnician;
      }
      if (workOrder.assignedTechnicianIds && Array.isArray(workOrder.assignedTechnicianIds)) {
        return workOrder.assignedTechnicianIds;
      }
      if (workOrder.assignedTechnician && workOrder.assignedTechnician.name) {
        return [workOrder.assignedTechnician];
      }
      if (workOrder.assignedTechnicianId && workOrder.assignedTechnicianId.name) {
        return [workOrder.assignedTechnicianId];
      }
      return [];
    };

    const technicians = getTechnicians();

    return (
      <div className="p-4">
        <Row className="g-4">
          {/* Assigned Technicians */}
          <Col md={8}>
            <motion.div variants={cardVariants} initial="hidden" animate="visible">
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="info-icon me-3">
                      <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <FeatherIcon icon="users" size={20} className="text-success" />
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">{t('shared.assigned_technicians', 'Assigned Technicians')}</h6>
                      <p className="text-muted small mb-0">{t('business.components.work_order_details.technicians.subtitle', 'Team members working on this order')}</p>
                    </div>
                  </div>

                  {technicians.length > 0 ? (
                    <div className="technicians-grid">
                      {technicians.map((technician, index) => (
                        <motion.div
                          key={technician.id || index}
                          className="technician-card bg-light rounded p-3 mb-3"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="technician-avatar me-3">
                              <div className="bg-success bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                                   style={{ width: '48px', height: '48px' }}>
                                <FeatherIcon icon="user" size={20} className="text-success" />
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-semibold">{technician.name}</h6>
                              {technician.role && (
                                <Badge color="light" className="text-muted">
                                  {technician.role.replace('ROLE_', '').replace('_', ' ')}
                                </Badge>
                              )}
                            </div>
                            <div className="technician-status">
                              <Badge color="success" pill>
                                <FeatherIcon icon="check" size={12} className="me-1" />
                                {t('shared.assigned', 'Assigned')}
                              </Badge>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                           style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                        <FeatherIcon icon="user-x" size={24} className="text-muted" />
                      </div>
                      <h6 className="text-muted">{t('business.components.work_order_details.technicians.no_technicians', 'No Technicians Assigned')}</h6>
                      <p className="text-muted mb-0">{t('business.components.work_order_details.technicians.no_technicians_desc', 'This work order doesn\'t have any assigned technicians yet')}</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </motion.div>
          </Col>

          {/* Reported By */}
          {workOrder.reportedBy && (
            <Col md={4}>
              <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
                <Card className="border-0 shadow-sm h-100">
                  <CardBody className="p-4 text-center">
                    <div className="mb-3">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto"
                           style={{ width: '48px', height: '48px' }}>
                        <FeatherIcon icon="user" size={24} className="text-primary" />
                      </div>
                    </div>
                    <h6 className="mb-2 fw-bold">{t('shared.reported_by', 'Reported By')}</h6>
                    <p className="mb-1 text-dark">{workOrder.reportedBy.name}</p>
                    {workOrder.reportedBy.role && (
                      <Badge color="light" className="text-muted">
                        {workOrder.reportedBy.role.replace('ROLE_', '').replace('_', ' ')}
                      </Badge>
                    )}
                  </CardBody>
                </Card>
              </motion.div>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  const renderMediaTab = () => {
    const isRoutine = workOrder.type === 'routine';

    const hasPreImages = isRoutine && workOrder.preImages && workOrder.preImages.length > 0;
    const hasPostImages = isRoutine && workOrder.postImages && workOrder.postImages.length > 0;

    // Check for post-service media (single URL strings from resolution)
    const hasPostServiceImage = workOrder.postServiceImage && workOrder.postServiceImage.length > 0;
    const hasPostServiceVideo = workOrder.postServiceVideo && workOrder.postServiceVideo.length > 0;
    const hasPostServiceDocument = workOrder.postServiceDocument && workOrder.postServiceDocument.length > 0;
    const hasPostServiceMedia = hasPostServiceImage || hasPostServiceVideo || hasPostServiceDocument;

    const hasOtherImages = (workOrder.imageUrls && workOrder.imageUrls.length > 0) ||
                           (workOrder.images && workOrder.images.length > 0) ||
                           (workOrder.imagesUrl && workOrder.imagesUrl.length > 0) ||
                           (workOrder.recallImageUrls && workOrder.recallImageUrls.length > 0) ||
                           hasPostServiceImage;

    const hasImages = hasPreImages || hasPostImages || hasOtherImages;

    const hasDocuments = (workOrder.documentUrls && workOrder.documentUrls.length > 0) ||
                        (workOrder.documents && workOrder.documents.length > 0) ||
                        (workOrder.recallDocumentUrls && workOrder.recallDocumentUrls.length > 0) ||
                        hasPostServiceDocument;

    const hasVideos = hasPostServiceVideo;

    const getAllImages = () => {
      let allImages = [];
      if (workOrder.imageUrls) allImages = [...allImages, ...workOrder.imageUrls];
      if (workOrder.images) allImages = [...allImages, ...workOrder.images];
      if (workOrder.imagesUrl) allImages = [...allImages, ...workOrder.imagesUrl];
      if (workOrder.recallImageUrls) allImages = [...allImages, ...workOrder.recallImageUrls];
      if (workOrder.postServiceImage) allImages = [...allImages, workOrder.postServiceImage];
      return allImages;
    };

    const getAllDocuments = () => {
      let allDocuments = [];
      if (workOrder.documentUrls) allDocuments = [...allDocuments, ...workOrder.documentUrls];
      if (workOrder.documents) allDocuments = [...allDocuments, ...workOrder.documents];
      if (workOrder.recallDocumentUrls) allDocuments = [...allDocuments, ...workOrder.recallDocumentUrls];
      if (workOrder.postServiceDocument) allDocuments = [...allDocuments, workOrder.postServiceDocument];
      return allDocuments;
    };

    return (
      <div className="p-4">
        {(hasImages || hasDocuments || hasVideos) ? (
          <Row className="g-4">
            {/* For Routine: Show Pre and Post Images separately */}
            {isRoutine ? (
              <>
                {/* Pre Images */}
                {hasPreImages && (
                  <Col md={hasPostImages ? 6 : (hasDocuments ? 8 : 12)}>
                    <motion.div variants={cardVariants} initial="hidden" animate="visible">
                      <Card className="border-0 shadow-sm">
                        <CardBody className="p-4">
                          <div className="d-flex align-items-center mb-3">
                            <div className="info-icon me-3">
                              <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                   style={{ width: '40px', height: '40px' }}>
                                <FeatherIcon icon="image" size={20} className="text-warning" />
                              </div>
                            </div>
                            <div>
                              <h6 className="mb-1 fw-bold">{t('shared.pre_images', 'Pre Images')}</h6>
                              <p className="text-muted small mb-0">{t('business.components.work_order_details.media.pre_images_subtitle', 'Images before maintenance')}</p>
                            </div>
                          </div>

                          <div className="images-grid">
                            <Row className="g-3">
                              {workOrder.preImages.map((image, index) => (
                                <Col key={index} md={6} sm={6}>
                                  <motion.div
                                    className="image-item"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <img
                                      src={image}
                                      alt={`Pre Image ${index + 1}`}
                                      className="img-fluid rounded shadow-sm"
                                      style={{
                                        width: '100%',
                                        height: '120px',
                                        objectFit: 'cover',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => window.open(image, '_blank')}
                                    />
                                  </motion.div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        </CardBody>
                      </Card>
                    </motion.div>
                  </Col>
                )}

                {/* Post Images */}
                {hasPostImages && (
                  <Col md={hasPreImages ? 6 : (hasDocuments ? 8 : 12)}>
                    <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
                      <Card className="border-0 shadow-sm">
                        <CardBody className="p-4">
                          <div className="d-flex align-items-center mb-3">
                            <div className="info-icon me-3">
                              <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                   style={{ width: '40px', height: '40px' }}>
                                <FeatherIcon icon="image" size={20} className="text-success" />
                              </div>
                            </div>
                            <div>
                              <h6 className="mb-1 fw-bold">{t('shared.post_images', 'Post Images')}</h6>
                              <p className="text-muted small mb-0">{t('business.components.work_order_details.media.post_images_subtitle', 'Images after maintenance')}</p>
                            </div>
                          </div>

                          <div className="images-grid">
                            <Row className="g-3">
                              {workOrder.postImages.map((image, index) => (
                                <Col key={index} md={6} sm={6}>
                                  <motion.div
                                    className="image-item"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <img
                                      src={image}
                                      alt={`Post Image ${index + 1}`}
                                      className="img-fluid rounded shadow-sm"
                                      style={{
                                        width: '100%',
                                        height: '120px',
                                        objectFit: 'cover',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => window.open(image, '_blank')}
                                    />
                                  </motion.div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        </CardBody>
                      </Card>
                    </motion.div>
                  </Col>
                )}
              </>
            ) : (
              /* For Non-Routine: Show all images together */
              hasImages && (
                <Col md={hasDocuments ? 8 : 12}>
                  <motion.div variants={cardVariants} initial="hidden" animate="visible">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="info-icon me-3">
                            <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                 style={{ width: '40px', height: '40px' }}>
                              <FeatherIcon icon="image" size={20} className="text-primary" />
                            </div>
                          </div>
                          <div>
                            <h6 className="mb-1 fw-bold">{t('shared.images', 'Images')}</h6>
                            <p className="text-muted small mb-0">{t('business.components.work_order_details.media.images_subtitle', 'Attached images and photos')}</p>
                          </div>
                        </div>

                        <div className="images-grid">
                          <Row className="g-3">
                            {getAllImages().map((image, index) => (
                              <Col key={index} md={4} sm={6}>
                                <motion.div
                                  className="image-item"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <img
                                    src={image}
                                    alt={`Attachment ${index + 1}`}
                                    className="img-fluid rounded shadow-sm"
                                    style={{
                                      width: '100%',
                                      height: '120px',
                                      objectFit: 'cover',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => window.open(image, '_blank')}
                                  />
                                </motion.div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      </CardBody>
                    </Card>
                  </motion.div>
                </Col>
              )
            )}

            {/* Documents */}
            {hasDocuments && (
              <Col md={hasImages ? 4 : 12}>
                <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="info-icon me-3">
                          <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                               style={{ width: '40px', height: '40px' }}>
                            <FeatherIcon icon="file-text" size={20} className="text-success" />
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-1 fw-bold">{t('shared.documents', 'Documents')}</h6>
                          <p className="text-muted small mb-0">{t('business.components.work_order_details.media.documents_subtitle', 'Attached documents')}</p>
                        </div>
                      </div>

                      <div className="documents-list">
                        {getAllDocuments().map((doc, index) => (
                          <motion.a
                            key={index}
                            href={doc}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="document-item d-flex align-items-center p-3 rounded bg-light text-decoration-none mb-2"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className="document-icon me-3">
                              <div className="bg-success bg-opacity-20 rounded d-flex align-items-center justify-content-center"
                                   style={{ width: '32px', height: '32px' }}>
                                <FeatherIcon icon="file-text" size={16} className="text-success" />
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-0 text-dark">{t('shared.document', 'Document')} {index + 1}</h6>
                              <small className="text-muted">{t('shared.click_to_view', 'Click to view')}</small>
                            </div>
                            <FeatherIcon icon="external-link" size={16} className="text-muted" />
                          </motion.a>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            )}

            {/* Videos Section - only shown when there are videos */}
            {hasVideos && (
              <Col md={12}>
                <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="info-icon me-3">
                          <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                               style={{ width: '40px', height: '40px' }}>
                            <FeatherIcon icon="video" size={20} className="text-danger" />
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-1 fw-bold">{t('shared.videos', 'Videos')}</h6>
                          <p className="text-muted small mb-0">{t('business.components.work_order_details.media.videos_subtitle', 'Attached video files')}</p>
                        </div>
                      </div>
                      <div className="videos-list">
                        <motion.a
                          href={workOrder.postServiceVideo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-item d-flex align-items-center p-3 rounded bg-light text-decoration-none"
                          whileHover={{ scale: 1.02, backgroundColor: '#e9ecef' }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="document-icon me-3">
                            <div className="bg-danger bg-opacity-20 rounded d-flex align-items-center justify-content-center"
                                 style={{ width: '32px', height: '32px' }}>
                              <FeatherIcon icon="video" size={16} className="text-danger" />
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-0 text-dark">{t('business.components.work_order_details.media.post_service_video', 'Post Service Video')}</h6>
                            <small className="text-muted">{t('shared.click_to_view', 'Click to view')}</small>
                          </div>
                          <FeatherIcon icon="external-link" size={16} className="text-muted" />
                        </motion.a>
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            )}
          </Row>
        ) : (
          <div className="text-center py-5">
            <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                 style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
              <FeatherIcon icon="paperclip" size={24} className="text-muted" />
            </div>
            <h6 className="text-muted">{t('business.components.work_order_details.media.no_attachments', 'No Attachments')}</h6>
            <p className="text-muted mb-0">{t('business.components.work_order_details.media.no_attachments_desc', 'This work order doesn\'t have any images or documents attached')}</p>
          </div>
        )}
      </div>
    );
  };

  const renderChecklistTab = () => {
    const hasChecklist = workOrder.checkLists && workOrder.checkLists.length > 0;

    const handleEditClick = (item) => {
      setEditingChecklistItem(item.id);
      setEditedChecklistName(item.checkListName);
      setEditedRemarks(item.remarks || '');
    };

    const handleSaveEdit = async (item) => {
      if (onChecklistUpdate) {
        await onChecklistUpdate({
          id: item.id,
          checkListName: editedChecklistName,
          remarks: editedRemarks,
          checked: item.checked
        });
      }
      setEditingChecklistItem(null);
    };

    const handleCancelEdit = () => {
      setEditingChecklistItem(null);
      setEditedChecklistName('');
      setEditedRemarks('');
    };

    const handleDelete = async (itemId) => {
      if (window.confirm(t('business.components.work_order_details.checklist.delete_confirm', 'Are you sure you want to delete this checklist item?'))) {
        if (onChecklistUpdate) {
          await onChecklistUpdate({ id: itemId, action: 'delete' });
        }
      }
    };

    return (
      <div className="p-4">
        {hasChecklist ? (
          <Row className="g-4">
            <Col md={12}>
              <motion.div variants={cardVariants} initial="hidden" animate="visible">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px' }}>
                          <FeatherIcon icon="check-square" size={20} className="text-primary" />
                        </div>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">{t('business.components.work_order_details.checklist.title', 'Work Order Checklist')}</h6>
                        <p className="text-muted small mb-0">
                          {t('business.components.work_order_details.checklist.items_completed', '{{completed}} of {{total}} items completed', {
                            completed: workOrder.checkLists.filter(item => item.checked).length,
                            total: workOrder.checkLists.length
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="checklist-items">
                      {workOrder.checkLists.map((item, index) => (
                        <motion.div
                          key={item.id || index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="checklist-item p-3 mb-3 rounded"
                          style={{
                            backgroundColor: item.checked ? '#f0fdf4' : '#f8f9fa',
                            border: `1px solid ${item.checked ? '#86efac' : '#e5e7eb'}`,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {editingChecklistItem === item.id ? (
                            // Edit Mode
                            <div className="d-flex flex-column gap-2">
                              <div className="d-flex gap-2">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editedChecklistName}
                                  onChange={(e) => setEditedChecklistName(e.target.value)}
                                  placeholder={t('business.components.work_order_details.checklist.item_name_placeholder', 'Checklist item name')}
                                />
                              </div>
                              <div className="d-flex gap-2">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={editedRemarks}
                                  onChange={(e) => setEditedRemarks(e.target.value)}
                                  placeholder={t('business.components.work_order_details.checklist.remarks_placeholder', 'Remarks (optional)')}
                                />
                              </div>
                              <div className="d-flex gap-2 justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-success d-flex align-items-center gap-1"
                                  onClick={() => handleSaveEdit(item)}
                                >
                                  <FeatherIcon icon="check" size={14} />
                                  {t('shared.save', 'Save')}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary d-flex align-items-center gap-1"
                                  onClick={handleCancelEdit}
                                >
                                  <FeatherIcon icon="x" size={14} />
                                  {t('shared.cancel', 'Cancel')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View Mode
                            <div className="d-flex align-items-start gap-3">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                onClick={async () => {
                                  if (onChecklistUpdate) {
                                    await onChecklistUpdate({
                                      id: item.id,
                                      checkListName: item.checkListName,
                                      remarks: item.remarks || '',
                                      checked: !item.checked
                                    });
                                  }
                                }}
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  backgroundColor: item.checked ? '#22c55e' : '#e5e7eb',
                                  minWidth: '28px',
                                  transition: 'all 0.3s ease',
                                  cursor: 'pointer'
                                }}
                              >
                                {item.checked && (
                                  <FeatherIcon icon="check" size={16} className="text-white" />
                                )}
                              </div>
                              <div className="flex-grow-1">
                                <p className={`mb-1 ${item.checked ? 'text-decoration-line-through text-muted' : 'fw-medium text-dark'}`}>
                                  {item.checkListName}
                                </p>
                                {item.remarks && (
                                  <small className="text-muted d-flex align-items-center gap-1">
                                    <FeatherIcon icon="message-square" size={12} />
                                    {item.remarks}
                                  </small>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Badge
                                  color={item.checked ? 'success' : 'secondary'}
                                  className="align-self-start"
                                  style={{ fontSize: '10px' }}
                                >
                                  {item.checked ? t('shared.completed', 'Completed') : t('shared.pending', 'Pending')}
                                </Badge>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center p-1"
                                  onClick={() => handleEditClick(item)}
                                  title={t('shared.edit', 'Edit')}
                                  style={{ width: '28px', height: '28px' }}
                                >
                                  <FeatherIcon icon="edit-2" size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center p-1"
                                  onClick={() => handleDelete(item.id)}
                                  title={t('shared.delete', 'Delete')}
                                  style={{ width: '28px', height: '28px' }}
                                >
                                  <FeatherIcon icon="trash-2" size={14} />
                                </button>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted fw-medium">{t('business.components.work_order_details.checklist.overall_progress', 'Overall Progress')}</small>
                        <small className="text-primary fw-bold">
                          {Math.round((workOrder.checkLists.filter(item => item.checked).length / workOrder.checkLists.length) * 100)}%
                        </small>
                      </div>
                      <Progress
                        value={(workOrder.checkLists.filter(item => item.checked).length / workOrder.checkLists.length) * 100}
                        color="success"
                        style={{ height: '8px' }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            </Col>
          </Row>
        ) : (
          <div className="text-center py-5">
            <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                 style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
              <FeatherIcon icon="check-square" size={24} className="text-muted" />
            </div>
            <h6 className="text-muted">{t('business.components.work_order_details.checklist.no_checklist', 'No Checklist')}</h6>
            <p className="text-muted mb-0">{t('business.components.work_order_details.checklist.no_checklist_desc', 'This work order doesn\'t have a checklist')}</p>
          </div>
        )}
      </div>
    );
  };

  const renderActivityTab = () => (
    <div className="p-4">
      <Row>
        <Col>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="info-icon me-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '40px', height: '40px' }}>
                      <FeatherIcon icon="activity" size={20} className="text-primary" />
                    </div>
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold">{t('shared.activity_timeline', 'Activity Timeline')}</h6>
                    <p className="text-muted small mb-0">{t('business.components.work_order_details.activity.subtitle', 'Track all updates and comments')}</p>
                  </div>
                </div>

                {/* Activity Timeline */}
                <div className="activity-timeline mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {loadingActivities ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="text-muted mt-2">{t('shared.loading', 'Loading...')}</p>
                    </div>
                  ) : activityForbidden ? (
                    <div className="text-center py-5">
                      <FeatherIcon icon="lock" size={28} className="text-muted mb-2" />
                      <p className="text-muted mb-0">
                        {t('teams.restrictVisibility.workOrderOutOfScope', "This work order is outside your team's scope.")}
                      </p>
                    </div>
                  ) : activities && activities.length > 0 ? (
                    <div className="timeline">
                      {activities.map((activity, index) => (
                        <motion.div
                          key={activity.id || index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="timeline-item mb-4"
                          style={{ position: 'relative', paddingLeft: '40px' }}
                        >
                          {/* Timeline dot */}
                          <div
                            className="timeline-dot bg-primary"
                            style={{
                              position: 'absolute',
                              left: '0',
                              top: '8px',
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              border: '3px solid #fff',
                              boxShadow: '0 0 0 2px #0d6efd'
                            }}
                          />
                          {/* Timeline line */}
                          {index < activities.length - 1 && (
                            <div
                              style={{
                                position: 'absolute',
                                left: '5px',
                                top: '20px',
                                width: '2px',
                                height: 'calc(100% + 16px)',
                                backgroundColor: '#e9ecef'
                              }}
                            />
                          )}
                          {/* Activity content */}
                          <div className="bg-light rounded p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex align-items-center">
                                <FeatherIcon icon="user" size={14} className="text-muted me-2" />
                                <span className="fw-semibold text-dark">
                                  {activity.userName || activity.user?.name || t('shared.system', 'System')}
                                </span>
                              </div>
                              <small className="text-muted">
                                {activity.createdAt ? formatCreatedDate(activity.createdAt) : ''}
                                {activity.createdAt && (
                                  <span className="ms-1">
                                    {new Date(activity.createdAt).toLocaleTimeString(navigator.language || 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </small>
                            </div>
                            <p className="mb-0 text-secondary small">
                              {translateActivity(activity, t)}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                           style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                        <FeatherIcon icon="message-square" size={24} className="text-muted" />
                      </div>
                      <h6 className="text-muted">{t('business.components.work_order_details.activity.no_activity', 'No Activity Yet')}</h6>
                      <p className="text-muted mb-0">{t('business.components.work_order_details.activity.no_activity_desc', 'Be the first to add a comment')}</p>
                    </div>
                  )}
                </div>

                {/* Comment Form */}
                <div className="comment-form mt-4 pt-4 border-top">
                  <h6 className="mb-3 fw-semibold">
                    <FeatherIcon icon="message-circle" size={16} className="me-2" />
                    {t('shared.add_comment', 'Add Comment')}
                  </h6>
                  <Form onSubmit={handleCommentSubmit}>
                    <div className="mb-3">
                      <Input
                        type="textarea"
                        rows="3"
                        placeholder={t('business.components.work_order_details.activity.comment_placeholder', 'Write your comment here...')}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        className="form-control"
                        disabled={submittingComment}
                      />
                    </div>
                    <div className="d-flex justify-content-end">
                      <Button
                        color="primary"
                        type="submit"
                        disabled={!commentText.trim() || submittingComment}
                        className="d-flex align-items-center"
                      >
                        {submittingComment ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t('shared.submitting', 'Submitting...')}
                          </>
                        ) : (
                          <>
                            <FeatherIcon icon="send" size={16} className="me-2" />
                            {t('shared.submit_comment', 'Submit Comment')}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        </Col>
      </Row>
    </div>
  );

  // Render Sub Work Orders Tab
  const renderSubWorkOrdersTab = () => (
    <div className="p-4">
      <Row>
        <Col>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div className="d-flex align-items-center">
                    <div className="info-icon me-3">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <FeatherIcon icon="repeat" size={20} className="text-primary" />
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">{t('business.components.work_order_details.sub_work_orders.title', 'Scheduled Occurrences')}</h6>
                      <p className="text-muted small mb-0">
                        {t('business.components.work_order_details.sub_work_orders.completed_count', '{{completed}} of {{total}} completed', {
                          completed: workOrder?.completedOccurrences || 0,
                          total: subWorkOrders.length
                        })}
                      </p>
                    </div>
                  </div>
                  {subWorkOrders.length > 0 && (
                    <div className="text-end">
                      <Progress
                        value={subWorkOrders.length > 0 ? ((workOrder?.completedOccurrences || 0) / subWorkOrders.length) * 100 : 0}
                        color="success"
                        style={{ width: '150px', height: '8px' }}
                        className="mb-1"
                      />
                      <small className="text-muted">
                        {Math.round(subWorkOrders.length > 0 ? ((workOrder?.completedOccurrences || 0) / subWorkOrders.length) * 100 : 0)}% {t('shared.complete', 'Complete')}
                      </small>
                    </div>
                  )}
                </div>

                {loadingSubWorkOrders ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="text-muted mt-2">{t('business.components.work_order_details.sub_work_orders.loading', 'Loading sub-work orders...')}</p>
                  </div>
                ) : subWorkOrders.length > 0 ? (
                  <div className="sub-work-orders-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '50px' }}>#</th>
                          <th>{t('shared.scheduled_date', 'Scheduled Date')}</th>
                          <th>{t('shared.status', 'Status')}</th>
                          <th>{t('shared.asset_status', 'Asset Status')}</th>
                          <th>{t('shared.technician', 'Technician')}</th>
                          <th>{t('shared.remarks', 'Remarks')}</th>
                          <th style={{ width: '100px' }}>{t('shared.actions', 'Actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subWorkOrders.map((subWO, index) => (
                          <tr key={subWO.id}>
                            <td className="fw-semibold">{subWO.recurrenceNumber || index + 1}</td>
                            <td>
                              <small className="text-muted">
                                <FeatherIcon icon="calendar" size={12} className="me-1" />
                                {subWO.scheduledDate ? new Date(subWO.scheduledDate).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) : '-'}
                              </small>
                            </td>
                            <td>
                              {editingSubWorkOrder?.id === subWO.id ? (
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  value={editingSubWorkOrder.status || 'PENDING'}
                                  onChange={(e) => setEditingSubWorkOrder({...editingSubWorkOrder, status: e.target.value})}
                                >
                                  <option value="PENDING">Pending</option>
                                  <option value="IN_PROGRESS">In Progress</option>
                                  <option value="RESOLVED">Resolved</option>
                                </Input>
                              ) : (
                                <Badge color={
                                  subWO.status === 'RESOLVED' ? 'success' :
                                  subWO.status === 'IN_PROGRESS' ? 'warning' : 'secondary'
                                }>
                                  {subWO.status || 'PENDING'}
                                </Badge>
                              )}
                            </td>
                            <td>
                              {editingSubWorkOrder?.id === subWO.id ? (
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  value={editingSubWorkOrder.usageStatus || ''}
                                  onChange={(e) => setEditingSubWorkOrder({...editingSubWorkOrder, usageStatus: e.target.value})}
                                >
                                  <option value="">{t('shared.select', 'Select...')}</option>
                                  <option value="ONLINE">{t('shared.online', 'Online')}</option>
                                  <option value="OFFLINE">{t('shared.offline', 'Offline')}</option>
                                  <option value="DO_NOT_TRACK">{t('shared.do_not_track', 'Do Not Track')}</option>
                                </Input>
                              ) : (
                                subWO.usageStatus ? (
                                  <Badge color="primary" style={{ fontSize: '11px', padding: '4px 8px' }}>
                                    {getUsageStatusLabel(subWO.usageStatus)}
                                  </Badge>
                                ) : <small className="text-muted">-</small>
                              )}
                            </td>
                            <td>
                              <small>
                                {subWO.assignedTechnician?.name || '-'}
                              </small>
                            </td>
                            <td>
                              {editingSubWorkOrder?.id === subWO.id ? (
                                <Input
                                  type="text"
                                  bsSize="sm"
                                  placeholder={t('business.components.work_order_details.sub_work_orders.enter_remarks', 'Enter remarks...')}
                                  value={editingSubWorkOrder.remarks || ''}
                                  onChange={(e) => setEditingSubWorkOrder({...editingSubWorkOrder, remarks: e.target.value})}
                                />
                              ) : (
                                <small className="text-muted">{subWO.remarks || '-'}</small>
                              )}
                            </td>
                            <td>
                              {editingSubWorkOrder?.id === subWO.id ? (
                                <div className="d-flex gap-1">
                                  <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => handleUpdateSubWorkOrder(editingSubWorkOrder)}
                                    title={t('shared.save', 'Save')}
                                  >
                                    <FeatherIcon icon="check" size={12} />
                                  </Button>
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={() => setEditingSubWorkOrder(null)}
                                    title={t('shared.cancel', 'Cancel')}
                                  >
                                    <FeatherIcon icon="x" size={12} />
                                  </Button>
                                </div>
                              ) : (
                                <div className="d-flex gap-1">
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => setEditingSubWorkOrder({...subWO})}
                                    title={t('shared.edit', 'Edit')}
                                  >
                                    <FeatherIcon icon="edit-2" size={12} />
                                  </Button>
                                  {subWO.status !== 'RESOLVED' && (
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="text-danger"
                                      onClick={() => handleDeleteSubWorkOrder(subWO.id)}
                                      title={t('shared.delete', 'Delete')}
                                    >
                                      <FeatherIcon icon="trash-2" size={12} />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                         style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                      <FeatherIcon icon="calendar" size={24} className="text-muted" />
                    </div>
                    <h6 className="text-muted">{t('business.components.work_order_details.sub_work_orders.no_occurrences', 'No Scheduled Occurrences')}</h6>
                    <p className="text-muted mb-0">{t('business.components.work_order_details.sub_work_orders.no_occurrences_desc', 'Sub-work orders will appear here once generated.')}</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </motion.div>
        </Col>
      </Row>
    </div>
  );

  const renderToolsTab = () => (
    <div className="p-4">
      <Row className="g-4">
        {/* Required Tools Section - commented out for now
        <Col md={12}>
          <motion.div variants={cardVariants} initial="hidden" animate="visible">
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center">
                    <div className="info-icon me-3">
                      <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <FeatherIcon icon="list" size={20} className="text-info" />
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">{t('business.tools.required_tools', 'Required Tools')}</h6>
                      <p className="text-muted small mb-0">{t('business.tools.required_tools_desc', 'Tools needed for this work order')}</p>
                    </div>
                  </div>
                </div>

                {loadingRequiredTools ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : (
                  <>
                    {requiredTools.length > 0 && (
                      <div className="mb-3">
                        {requiredTools.map((rt) => (
                          <div key={rt.id} className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2">
                            <div>
                              <span className="fw-medium">{rt.toolName}</span>
                              <Badge color="primary" className="ms-2" pill>{t('business.tools.qty', 'Qty')}: {rt.quantity}</Badge>
                              {rt.notes && <small className="text-muted ms-2">{rt.notes}</small>}
                            </div>
                            <PermissionGuard permission="tool:delete">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteRequiredTool(rt.id)}
                              >
                                <FeatherIcon icon="trash-2" size={14} />
                              </button>
                            </PermissionGuard>
                          </div>
                        ))}
                      </div>
                    )}

                    <PermissionGuard permission="tool:create">
                      <div className="border rounded p-3 bg-light">
                        <div className="d-flex gap-2 align-items-end">
                          <div className="flex-grow-1">
                            <label className="form-label small">{t('shared.tool_name', 'Tool Name')}</label>
                            <Input
                              type="text"
                              bsSize="sm"
                              value={newRequiredTool.toolName}
                              onChange={(e) => setNewRequiredTool({...newRequiredTool, toolName: e.target.value})}
                              placeholder={t('business.tools.enter_tool_name', 'Enter tool name')}
                            />
                          </div>
                          <div style={{ width: '80px' }}>
                            <label className="form-label small">{t('business.tools.qty', 'Qty')}</label>
                            <Input
                              type="number"
                              bsSize="sm"
                              min="1"
                              value={newRequiredTool.quantity}
                              onChange={(e) => setNewRequiredTool({...newRequiredTool, quantity: e.target.value})}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <label className="form-label small">{t('business.tools.notes', 'Notes')}</label>
                            <Input
                              type="text"
                              bsSize="sm"
                              value={newRequiredTool.notes}
                              onChange={(e) => setNewRequiredTool({...newRequiredTool, notes: e.target.value})}
                              placeholder={t('business.tools.optional_notes', 'Optional notes')}
                            />
                          </div>
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleAddRequiredTool}
                            disabled={!newRequiredTool.toolName.trim()}
                          >
                            <FeatherIcon icon="plus" size={14} className="me-1" />
                            {t('shared.add', 'Add')}
                          </Button>
                        </div>
                      </div>
                    </PermissionGuard>
                  </>
                )}
              </CardBody>
            </Card>
          </motion.div>
        </Col>
        */}

        {/* Checked Out Tools Section */}
        <Col md={12}>
          <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div className="d-flex align-items-center">
                    <div className="info-icon me-3">
                      <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                           style={{ width: '40px', height: '40px' }}>
                        <FeatherIcon icon="tool" size={20} className="text-primary" />
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold">{t('business.tools.checked_out_tools', 'Checked Out Tools')}</h5>
                      <p className="text-muted small mb-0">{t('business.tools.checked_out_tools_desc', 'Tools currently in use for this work order')}</p>
                    </div>
                  </div>
                  <PermissionGuard permission="tool:update">
                    <Button
                      color="success"
                      size="sm"
                      className="d-flex align-items-center gap-2"
                      onClick={() => setToolCheckoutModal(true)}
                    >
                      <FeatherIcon icon="plus" size={16} />
                      {t('business.tools.check_out', 'Check Out')}
                    </Button>
                  </PermissionGuard>
                </div>

                {loadingCheckedOutTools ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" size="lg" />
                    <p className="text-muted mt-3">{t('shared.loading', 'Loading...')}</p>
                  </div>
                ) : checkedOutTools.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>#</th>
                          <th>{t('shared.tool_name', 'Tool Name')}</th>
                          <th>{t('shared.user', 'User')}</th>
                          <th>{t('shared.date', 'Date')}</th>
                          <th>{t('shared.condition', 'Condition')}</th>
                          <th>{t('shared.status', 'Status')}</th>
                          <th>{t('shared.actions', 'Actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checkedOutTools.map((ct, index) => (
                          <tr key={ct.id}>
                            <td>{index + 1}</td>
                            <td>
                              <span className="fw-medium">{ct.tool?.name || t('shared.n_a', 'N/A')}</span>
                              {ct.tool?.serialNumber && (
                                <small className="d-block text-muted">{ct.tool.serialNumber}</small>
                              )}
                            </td>
                            <td><small>{ct.checkedOutByUser?.name || ct.user?.name || ct.checkedOutByName || '-'}</small></td>
                            <td className="text-muted">{formatToolDate(ct.checkedOutAt)}</td>
                            <td>
                              {ct.conditionAtCheckout && (
                                <Badge color={
                                  ct.conditionAtCheckout === 'EXCELLENT' ? 'success' :
                                  ct.conditionAtCheckout === 'GOOD' ? 'primary' :
                                  ct.conditionAtCheckout === 'FAIR' ? 'warning' : 'danger'
                                } pill>
                                  {ct.conditionAtCheckout}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <Badge color={ct.status === 'CHECKED_OUT' ? 'warning' : 'success'} pill>
                                {ct.status === 'CHECKED_OUT' ? t('business.tools.status_checked_out', 'Checked Out') : t('business.tools.status_returned', 'Returned')}
                              </Badge>
                            </td>
                            <td>
                              {ct.status === 'CHECKED_OUT' && (
                                <PermissionGuard permission="tool:update">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => handleToolCheckinFromWO(ct.id)}
                                  >
                                    <FeatherIcon icon="log-in" size={14} className="me-1" />
                                    {t('business.tools.check_in', 'Check In')}
                                  </button>
                                </PermissionGuard>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                         style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                      <FeatherIcon icon="tool" size={24} className="text-muted" />
                    </div>
                    <h5 className="text-muted">{t('business.tools.no_checked_out_tools', 'No tools checked out for this work order')}</h5>
                  </div>
                )}
              </CardBody>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Tool Checkout Modal from WO context */}
      <ToolCheckoutModal
        isOpen={toolCheckoutModal}
        toggle={() => setToolCheckoutModal(false)}
        workOrderId={workOrder?.id}
        onSuccess={handleToolCheckoutSuccess}
      />

      {/* Tool Checkin Modal from WO context */}
      <ToolCheckinModal
        isOpen={toolCheckinModal}
        toggle={() => { setToolCheckinModal(false); setToolCheckinId(null); }}
        checkoutId={toolCheckinId}
        onSuccess={handleToolCheckinSuccess}
      />
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          toggle={toggle}
          size="xl"
          centered
          className="modern-modal"
          backdrop="static"
          style={{ maxHeight: '90vh' }}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-content border-0 shadow-lg"
            style={{ borderRadius: '12px', overflow: 'hidden' }}
          >
            {/* Enhanced Header */}
            <div
              className="modal-header border-0 text-white position-sticky d-flex justify-content-between align-items-center"
              style={{
                top: 0,
                zIndex: 10,
                background: getWorkOrderColor(workOrder.type),
                padding: '1.5rem'
              }}
            >
              <div className="d-flex align-items-center">
                <div className="ticket-icon me-3">
                  <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                       style={{ width: '48px', height: '48px' }}>
                    <FeatherIcon icon={getWorkOrderIcon(workOrder.type)} size={24} className="text-white" />
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h4 className="modal-title mb-1 text-white d-flex align-items-center">
                    <span className="me-2">{workOrder.eventTitle || workOrder.title || t('shared.work_order', 'Work Order')}</span>
                    <div className="d-flex align-items-center gap-2">
                      <WorkOrderStatusBadge status={workOrder?.calibrationStatus || workOrder.status} />
                      {workOrder.urgencyLevel && getPriorityBadge(workOrder.urgencyLevel)}
                    </div>
                  </h4>
                  <div className="d-flex align-items-center gap-3">
                    <span className="text-white small">
                      <FeatherIcon icon="calendar" size={14} className="me-1" />
                      {formatCreatedDate(workOrder.createdAt)}
                    </span>
                    {workOrder.type && (
                      <span className="text-white small">
                        <FeatherIcon icon="tag" size={14} className="me-1" />
                        {workOrder.type.toUpperCase().replace('-', ' ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 ms-auto">
                {onEdit && ['calibration', 'recall', 'routine', 'testing'].includes(workOrder.type) && (
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                    onClick={() => onEdit(workOrder)}
                    title={t('shared.edit_work_order', 'Edit Work Order')}
                    style={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      color: '#495057',
                      fontWeight: '500',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 1)';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <FeatherIcon icon="edit-3" size={16} />
                    <span>{t('shared.edit', 'Edit')}</span>
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={toggle}
                  aria-label={t('shared.close', 'Close')}
                ></button>
              </div>
            </div>

            {/* Enhanced Navigation */}
            <Nav
              tabs
              className="border-bottom-0 px-4 bg-light"
            >
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('details')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="file-text" size={16} className="me-2" />
                  {t('shared.details', 'Details')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'asset' ? 'active' : ''}`}
                  onClick={() => setActiveTab('asset')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="package" size={16} className="me-2" />
                  {t('shared.asset_location', 'Asset & Location')}
                </NavLink>
              </NavItem>
              {workOrder.type !== 'calibration' && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'technicians' ? 'active' : ''}`}
                    onClick={() => setActiveTab('technicians')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="users" size={16} className="me-2" />
                    {t('shared.team', 'Team')}
                  </NavLink>
                </NavItem>
              )}
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'media' ? 'active' : ''}`}
                  onClick={() => setActiveTab('media')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="paperclip" size={16} className="me-2" />
                  {t('shared.media', 'Media')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'tools' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tools')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="tool" size={16} className="me-2" />
                  {t('shared.tools', 'Tools')}
                </NavLink>
              </NavItem>
              {workOrder.type === 'routine' && workOrder.checkLists && workOrder.checkLists.length > 0 && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'checklist' ? 'active' : ''}`}
                    onClick={() => setActiveTab('checklist')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="check-square" size={16} className="me-2" />
                    {t('shared.checklist', 'Checklist')}
                  </NavLink>
                </NavItem>
              )}
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'timeAndCost' ? 'active' : ''}`}
                  onClick={() => setActiveTab('timeAndCost')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="clock" size={16} className="me-2" />
                  {t('cost_tracking.time_and_cost_tab', 'Time & Cost')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'activity' ? 'active' : ''}`}
                  onClick={() => setActiveTab('activity')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <FeatherIcon icon="activity" size={16} className="me-2" />
                  {t('shared.activity', 'Activity')}
                </NavLink>
              </NavItem>
              {/* Meters Tab - visible when WO has an asset */}
              {(workOrder?.asset?.id || workOrder?.assetId) && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'meters' ? 'active' : ''}`}
                    onClick={() => setActiveTab('meters')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="activity" size={16} className="me-2" />
                    {t('shared.meters', 'Meters')}
                    {woMeters.length > 0 && (
                      <Badge color="primary" pill className="ms-2">
                        {woMeters.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
              {/* Sub Work Orders Tab - visible for recurring work orders */}
              {workOrder?.recurrenceType && workOrder.recurrenceType !== 'NONE' && (
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'subworkorders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subworkorders')}
                    style={{
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      padding: '12px 20px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                  >
                    <FeatherIcon icon="repeat" size={16} className="me-2" />
                    {t('shared.sub_work_orders', 'Sub Work Orders')}
                    {subWorkOrders.length > 0 && (
                      <Badge color="primary" pill className="ms-2">
                        {subWorkOrders.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              )}
            </Nav>

            {/* Modal Body */}
            <div
              className="modal-body p-0"
              style={{
                maxHeight: 'calc(90vh - 280px)',
                overflow: 'auto'
              }}
            >
              <TabContent activeTab={activeTab} className="h-100">
                <TabPane tabId="details" className="h-100">
                  {renderBasicInfoTab()}
                </TabPane>
                <TabPane tabId="asset" className="h-100">
                  {renderAssetTab()}
                </TabPane>
                {workOrder.type !== 'calibration' && (
                  <TabPane tabId="technicians" className="h-100">
                    {renderTechniciansTab()}
                  </TabPane>
                )}
                <TabPane tabId="media" className="h-100">
                  {renderMediaTab()}
                </TabPane>
                <TabPane tabId="tools" className="h-100">
                  {renderToolsTab()}
                </TabPane>
                {workOrder.type === 'routine' && (
                  <TabPane tabId="checklist" className="h-100">
                    {renderChecklistTab()}
                  </TabPane>
                )}
                <TabPane tabId="activity" className="h-100">
                  {renderActivityTab()}
                </TabPane>
                <TabPane tabId="timeAndCost" className="h-100">
                  <div className="p-4">
                    {workOrder?.id ? (() => {
                      let currentUserId = null;
                      try {
                        const stored = localStorage.getItem('authUser');
                        currentUserId = stored ? JSON.parse(stored)?.id : null;
                      } catch (e) {
                        currentUserId = null;
                      }
                      return (
                        <>
                          <WoCostSection
                            workOrderId={workOrder.id}
                            workOrderStatus={workOrder?.status}
                            currentUserId={currentUserId}
                          />
                          <TimeEntrySection
                            workOrderId={workOrder.id}
                            workOrderStatus={workOrder?.status}
                            currentUserId={currentUserId}
                          />
                          <div className="mt-4">
                            <StatusTimeline workOrderId={workOrder.id} />
                          </div>
                        </>
                      );
                    })() : null}
                  </div>
                </TabPane>
                {/* Meters Tab Content */}
                {(workOrder?.asset?.id || workOrder?.assetId) && (
                  <TabPane tabId="meters" className="h-100">
                    {renderMetersTab()}
                  </TabPane>
                )}
                {/* Sub Work Orders Tab Content */}
                {workOrder?.recurrenceType && workOrder.recurrenceType !== 'NONE' && (
                  <TabPane tabId="subworkorders" className="h-100">
                    {renderSubWorkOrdersTab()}
                  </TabPane>
                )}
              </TabContent>
            </div>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default WorkOrderDetailsModal;